import React, { useState } from 'react';
import { Plus, Mail, Edit, Trash2, Eye, Calendar } from 'lucide-react';

// Components
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

// Hooks
import { useEmailTemplates } from '../../hooks/email/useEmailTemplates';

// Services
import { getTenantIdFromStorage } from '../../services/authentication/supertokens';

// Types
import { EmailTemplate, CreateEmailTemplatePayload, UpdateEmailTemplatePayload } from '../../services/email/emailTemplateService';

interface CreateTemplateFormData {
  template_type: 'ticket_confirmation' | 'reminder' | 'follow_up';
  subject_line: string;
  custom_message: string;
  enabled: boolean;
}

function EmailTemplatesTab() {
  const tenantId = getTenantIdFromStorage();
  const {
    templates,
    loading,
    error,
    creating,
    updating,
    deleting,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    refetch
  } = useEmailTemplates();

  const [localError, setLocalError] = useState<string | null>(null);
  
  // Combine hook error and local error
  const displayError = error || localError;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  const [createFormData, setCreateFormData] = useState<CreateTemplateFormData>({
    template_type: 'ticket_confirmation',
    subject_line: '',
    custom_message: '',
    enabled: true,
  });

  const [editFormData, setEditFormData] = useState<UpdateEmailTemplatePayload>({
    subject_line: '',
    custom_message: '',
    enabled: true,
  });

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if a template of this type already exists
    const existingTemplate = templates.find(t => t.template_type === createFormData.template_type);
    if (existingTemplate) {
      setLocalError(`A ${getTemplateTypeLabel(createFormData.template_type)} template already exists. You can only have one template per type.`);
      return;
    }
    
    try {
      const payload: CreateEmailTemplatePayload = {
        event_uuid: null,
        template_type: createFormData.template_type,
        subject_line: createFormData.subject_line || null,
        custom_message: createFormData.custom_message || null,
        enabled: createFormData.enabled,
        is_org_default: true,
      };

      await createTemplate(payload);
      setIsCreateDialogOpen(false);
      setLocalError(null);
      setCreateFormData({
        template_type: 'ticket_confirmation',
        subject_line: '',
        custom_message: '',
        enabled: true,
      });
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTemplate) return;

    try {
      await updateTemplate(selectedTemplate.uuid, editFormData);
      setIsEditDialogOpen(false);
      setSelectedTemplate(null);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;

    try {
      await deleteTemplate(selectedTemplate.uuid);
      setIsDeleteDialogOpen(false);
      setSelectedTemplate(null);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const openEditDialog = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setEditFormData({
      subject_line: template.subject_line || '',
      custom_message: template.custom_message || '',
      enabled: template.enabled,
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsDeleteDialogOpen(true);
  };

  const getTemplateTypeLabel = (type: string) => {
    switch (type) {
      case 'ticket_confirmation':
        return 'Ticket Confirmation';
      case 'reminder':
        return 'Event Reminder';
      case 'follow_up':
        return 'Follow Up';
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded animate-pulse w-96" />
            <div className="h-3 bg-muted rounded animate-pulse w-80" />
            <div className="h-3 bg-muted rounded animate-pulse w-64" />
          </div>
          <div className="h-10 bg-muted rounded animate-pulse w-40" />
        </div>
        
        {/* Templates list skeleton */}
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-5 bg-muted rounded animate-pulse w-32" />
                      <div className="h-5 bg-muted rounded animate-pulse w-16" />
                      <div className="h-5 bg-muted rounded animate-pulse w-20" />
                    </div>
                    <div className="h-4 bg-muted rounded animate-pulse w-48" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-8 bg-muted rounded animate-pulse w-16" />
                    <div className="h-8 bg-muted rounded animate-pulse w-16" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div>
                    <div className="h-4 bg-muted rounded animate-pulse w-24 mb-2" />
                    <div className="h-4 bg-muted rounded animate-pulse w-full" />
                  </div>
                  <div>
                    <div className="h-4 bg-muted rounded animate-pulse w-32 mb-2" />
                    <div className="h-16 bg-muted rounded animate-pulse w-full" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <p className="text-muted-foreground mt-2 text-sm md:text-base">
            Customize your ticket confirmation emails by adding personalized messages that will appear in every confirmation email sent to your customers
          </p>
          <p className="text-sm text-amber-600 mt-1">
            Note: You can only create one template per organization
          </p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) setLocalError(null);
        }}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2l w-95-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg md:text-xl">Create Organization Email Template</DialogTitle>
              <DialogDescription className="text-sm">
                Add a custom message to your ticket confirmation emails that will be sent to all customers who purchase tickets
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template_type">Template Type</Label>
                <Select
                  value={createFormData.template_type}
                  onValueChange={(value: 'ticket_confirmation' | 'reminder' | 'follow_up') => {
                    setCreateFormData({ ...createFormData, template_type: value });
                    setLocalError(null); // Clear any previous errors when changing type
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem 
                      value="ticket_confirmation" 
                      disabled={templates.some(t => t.template_type === 'ticket_confirmation')}
                    >
                      Ticket Confirmation {templates.some(t => t.template_type === 'ticket_confirmation') && '(Already exists)'}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  This message will appear in ticket confirmation emails for all events in your organization.
                </p>
              </div>
              {/* Error inside modal */}
              {localError && (
                <Alert variant="destructive">
                  <AlertDescription>{localError}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="subject_line">Custom Subject Line (Optional)</Label>
                <Input
                  id="subject_line"
                  value={createFormData.subject_line}
                  onChange={(e) =>
                    setCreateFormData({ ...createFormData, subject_line: e.target.value })
                  }
                  placeholder="Your Custom Event Title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="custom_message">Custom Message</Label>
                <Textarea
                  id="custom_message"
                  value={createFormData.custom_message}
                  onChange={(e) =>
                    setCreateFormData({ ...createFormData, custom_message: e.target.value })
                  }
                  placeholder="Add your custom message here. This will appear in the ticket confirmation email sent to customers."
                  rows={4}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={createFormData.enabled}
                  onCheckedChange={(checked) =>
                    setCreateFormData({ ...createFormData, enabled: checked })
                  }
                />
                <Label>Enabled</Label>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button type="submit" disabled={creating} className="w-full sm:w-auto">
                  {creating ? 'Creating...' : 'Create Template'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="w-full sm:w-auto">
                  Cancel
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Templates List */}
      <div className="grid gap-4">
        {templates.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Mail className="h-10 w-10 md:h-12 md:w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-center">No email templates</h3>
              <p className="text-muted-foreground text-center mb-4 text-sm md:text-base">
                Customize your ticket confirmation emails by adding personalized messages that will be included in every confirmation email sent to your customers.
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          templates.map((template) => (
            <Card key={template.uuid}>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="flex flex-col sm:flex-row sm:items-center gap-2 text-base md:text-lg">
                      <span className="truncate">{getTemplateTypeLabel(template.template_type)}</span>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="secondary" className="text-xs">Organization Template</Badge>
                        {!template.enabled && (
                          <Badge variant="outline" className="text-xs">Disabled</Badge>
                        )}
                      </div>
                    </CardTitle>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      {template.event && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span className="truncate">{template.event.title}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 self-start sm:self-center w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(template)}
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4" />
                      <span className="ml-1 sm:hidden">Edit</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDeleteDialog(template)}
                      className="flex-1"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="ml-1 sm:hidden">Delete</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {template.subject_line && (
                    <div>
                      <Label className="text-sm font-medium">Subject Line:</Label>
                      <p className="text-sm break-words">{template.subject_line}</p>
                    </div>
                  )}
                  
                  {template.custom_message && (
                    <div>
                      <Label className="text-sm font-medium">Custom Message:</Label>
                      <p className="text-sm whitespace-pre-line bg-muted p-3 rounded break-words">
                        {template.custom_message}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2l w-95-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg md:text-xl">Edit Email Template</DialogTitle>
            <DialogDescription className="text-sm">
              Update your email template settings
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_subject_line">Subject Line (Optional)</Label>
              <Input
                id="edit_subject_line"
                value={editFormData.subject_line || ''}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, subject_line: e.target.value })
                }
                placeholder="Custom subject line"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_custom_message">Custom Message</Label>
              <Textarea
                id="edit_custom_message"
                value={editFormData.custom_message || ''}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, custom_message: e.target.value })
                }
                placeholder="Your custom message"
                rows={4}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={editFormData.enabled}
                onCheckedChange={(checked) =>
                  setEditFormData({ ...editFormData, enabled: checked })
                }
              />
              <Label>Enabled</Label>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button type="submit" disabled={updating} className="w-full sm:w-auto">
                {updating ? 'Updating...' : 'Update Template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-2l w-95-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg md:text-xl">Delete Email Template</DialogTitle>
            <DialogDescription className="text-sm">
              Are you sure you want to delete this email template? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="destructive"
              className="text-white w-full sm:w-auto"
              onClick={handleDelete}
              disabled={deleting}
            >
              Delete Template
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EmailTemplatesTab; 